/**
 * Location Service - Get user's country and location data
 */

/**
 * Get user's location using browser Geolocation API
 * @returns {Promise<{latitude: number, longitude: number, country: string, city: string}>}
 */
export const getUserLocation = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Use reverse geocoding to get country from coordinates
          const locationData = await reverseGeocode(latitude, longitude);
          
          // Store location in localStorage
          const locationInfo = {
            latitude,
            longitude,
            country: locationData.country,
            countryCode: locationData.countryCode,
            city: locationData.city,
            timestamp: new Date().toISOString(),
          };
          
          localStorage.setItem('userLocation', JSON.stringify(locationInfo));
          
          resolve(locationInfo);
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          // Still resolve with coordinates even if geocoding fails
          const basicInfo = {
            latitude,
            longitude,
            country: 'Unknown',
            countryCode: 'XX',
            city: 'Unknown',
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem('userLocation', JSON.stringify(basicInfo));
          resolve(basicInfo);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to retrieve location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 600000, // Cache for 10 minutes
      }
    );
  });
};

/**
 * Reverse geocode coordinates to get country and city
 * Using OpenStreetMap Nominatim API (free, no API key required)
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<{country: string, countryCode: string, city: string}>}
 */
const reverseGeocode = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
    );
    
    if (!response.ok) {
      throw new Error('Geocoding API request failed');
    }
    
    const data = await response.json();
    
    return {
      country: data.address?.country || 'Unknown',
      countryCode: data.address?.country_code?.toUpperCase() || 'XX',
      city: data.address?.city || data.address?.town || data.address?.village || 'Unknown',
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Get user's country using IP-based geolocation (fallback method)
 * This doesn't require user permission but is less accurate
 * @returns {Promise<{country: string, countryCode: string, city: string}>}
 */
export const getLocationByIP = async () => {
  try {
    // Using ipapi.co - free tier allows 1000 requests/day
    const response = await fetch('https://ipapi.co/json/');
    
    if (!response.ok) {
      throw new Error('IP geolocation failed');
    }
    
    const data = await response.json();
    
    const locationInfo = {
      country: data.country_name || 'Unknown',
      countryCode: data.country_code || 'XX',
      city: data.city || 'Unknown',
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem('userLocation', JSON.stringify(locationInfo));
    
    return locationInfo;
  } catch (error) {
    console.error('IP-based location failed:', error);
    throw error;
  }
};

/**
 * Request location with user-friendly dialog
 * Shows a custom message before requesting permission
 * @param {Function} onSuccess - Callback when location is obtained
 * @param {Function} onError - Callback when location fails
 * @param {Function} onSkip - Callback when user skips
 */
export const requestLocationPermission = (onSuccess, onError, onSkip) => {
  // First try to get location with GPS
  getUserLocation()
    .then((locationInfo) => {
      console.log('Location obtained:', locationInfo);
      if (onSuccess) onSuccess(locationInfo);
    })
    .catch((error) => {
      console.warn('GPS location failed, falling back to IP-based location:', error);
      
      // Fallback to IP-based location
      getLocationByIP()
        .then((locationInfo) => {
          console.log('IP-based location obtained:', locationInfo);
          if (onSuccess) onSuccess(locationInfo);
        })
        .catch((ipError) => {
          console.error('All location methods failed:', ipError);
          if (onError) onError(ipError);
        });
    });
};

/**
 * Get stored location from localStorage
 * @returns {Object|null}
 */
export const getStoredLocation = () => {
  try {
    const stored = localStorage.getItem('userLocation');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading stored location:', error);
    return null;
  }
};

/**
 * Check if location data is still valid (not older than 24 hours)
 * @returns {boolean}
 */
export const isLocationValid = () => {
  const location = getStoredLocation();
  if (!location || !location.timestamp) return false;
  
  const locationTime = new Date(location.timestamp);
  const now = new Date();
  const hoursDiff = (now - locationTime) / (1000 * 60 * 60);
  
  return hoursDiff < 24; // Valid for 24 hours
};
